import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
  getV1CompaniesCompanyIdSiteTypeahead,
  SiteState,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { FocusEvent, type JSX, useCallback, useEffect, useRef } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import useMaterialRates from '~hooks/useMaterialRates/useMaterialRates';
import { SiteTypeahead } from '~hooks/useSites/models';
import {
  EmptyMaterialRateItem,
  MATERIAL_RATE_COVERAGE_ERROR_TYPE,
  type ProjectFormSchemaInterface,
} from '~pages/Sales/Projects/projectFormSchema';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';

import { useProjectFormV2Context } from '../ProjectFormV2Context';

const MATERIAL_RATE_ROW_SPACING = 2;

// Per-row material-rates section. Lives under projectFormV2/components/
// (sibling to panels/) so it can use useFieldArray + useWatch — the panel-grep
// guard forbids reactive hooks only inside `panels/`, not the rest of
// projectFormV2/. This placement was deliberate: the rates section needs its
// own non-panel folder to own its per-row reactive subscriptions.
type Props = {
  parentIndex: number;
};

type MaterialRateFormRow = NonNullable<
  ProjectFormSchemaInterface['materialRates']
>[number];

// A machine-seeded row is identified by exact value match against the snapshot
// the seeding effect last wrote (plus empty id / not deleted, checked at the
// call site). Any user edit changes one of these fields and breaks machine
// ownership. `companyId` records which company the row was seeded under, so a
// vanished row can be attributed either to the user (same company) or to the
// company cascade wiping the whole array (different company).
type MachineSeededRowSnapshot = {
  companyId: string;
  materialId: string;
  materialRateBuyPrice: string;
  materialRateSellPrice: string;
  materialRateSiteId: string;
  unitOfMeasureId: string;
};

function rowMatchesMachineSnapshot(
  row: MaterialRateFormRow,
  snapshot: MachineSeededRowSnapshot,
) {
  return (
    (row.material?.id ?? '') === snapshot.materialId &&
    String(row.materialRateBuyPrice ?? '') === snapshot.materialRateBuyPrice &&
    String(row.materialRateSellPrice ?? '') === snapshot.materialRateSellPrice &&
    (row.materialRateSite?.id ?? '') === snapshot.materialRateSiteId &&
    (row.unitOfMeasure?.id ?? '') === snapshot.unitOfMeasureId
  );
}

export function ProjectMaterialRatesSection({ parentIndex }: Props): JSX.Element | null {
  const { userStore } = useStores();
  const { canCreateMaterialRate, canEditMaterialRate } = userStore.getPermissions();
  const canManageMaterialRates = canCreateMaterialRate && canEditMaterialRate;

  const { control, formState, getValues, setValue } =
    useFormContext<ProjectFormSchemaInterface>();
  const { flags, watchedSelectedCompany, defaultProject, isCopyAndEdit, watchedAccount } =
    useProjectFormV2Context();
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();
  const { getCompanyMaterialRatesTypeahead } = useMaterialRates();

  // Seeding only applies to a blank create: on edit and copy the dialog loads
  // the project's saved rates asynchronously into this same array, and a
  // seeded row would persist as a duplicate rate on submit.
  const isBlankCreate = !defaultProject?.id && !isCopyAndEdit;

  const companyId = watchedSelectedCompany?.id || defaultProject?.company?.id || '';

  // Watch this row's material + UoM so the per-row rate UI tracks user
  // edits (the legacy form read these via watch() at the
  // panel level; in v2 the panel is render-only, so the rate section
  // subscribes itself).
  const parentMaterial = useWatch({
    control,
    name: `projectMaterialTotals.${parentIndex}.material`,
  });
  const parentUom = useWatch({
    control,
    name: `projectMaterialTotals.${parentIndex}.unitOfMeasure`,
  });
  const materialId = parentMaterial?.id ?? '';
  const materialName = parentMaterial?.name ?? '';
  const unitOfMeasureId = parentUom?.id ?? '';
  const unitOfMeasureName = parentUom?.name ?? '';

  // The project-level default site scopes the seeding typeahead the same way
  // the pickup site scopes the order form's rate resolution.
  const watchedProjectSite = useWatch({ control, name: 'site' });

  // Watch the full materialRates array; we render one sub-row per rate
  // whose parentIndex matches this section. The useFieldArray below owns
  // append/remove/update — fields() and watched values share the same
  // underlying form state.
  const watchedRates = useWatch({ control, name: 'materialRates' }) ?? [];

  const {
    fields: materialRateFields,
    append: appendMaterialRate,
    remove: removeMaterialRate,
    update: updateMaterialRate,
  } = useFieldArray({ control, name: 'materialRates' });

  const ratesForThisMaterial = watchedRates
    .map((rate, index) => ({ rate, fieldIndex: index }))
    .filter(({ rate }) => !rate?.isDeleted && rate?.parentIndex === parentIndex);

  const formatPriceOnBlur = useCallback(
    (
      fieldName:
        | `materialRates.${number}.materialRateBuyPrice`
        | `materialRates.${number}.materialRateSellPrice`,
    ) =>
      (e: FocusEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val !== '' && !isNaN(Number(val))) {
          // Yup schema types this as `number`, but the form actually holds
          // `string | number` during editing (matches EmptyMaterialRateItem).
          // The schema's `.transform()` coerces back to number at
          // validate/submit time. `as never` bridges the InferType view to the
          // runtime shape per the same pattern used by the cascading-reset in
          // ProjectFormV2Context.
          setValue(fieldName, Number(val).toFixed(2) as never, { shouldDirty: false });
        }
      },
    [setValue],
  );

  const fetchSites = useCallback(
    async (additionalProps: { query?: string; cursor?: string } = {}) => {
      if (!companyId) {
        return { data: [] };
      }

      try {
        const response = await getV1CompaniesCompanyIdSiteTypeahead({
          path: { 'company-id': companyId },
          query: {
            'search[query]': additionalProps.query || undefined,
            'page[limit]': DEFAULT_API_PAGE_SIZE,
            'filter[states]': [SiteState.ACTIVE],
            ...(additionalProps.cursor ? { 'page[after]': additionalProps.cursor } : {}),
          },
        });

        const sites = response.data.data.map((site) => {
          const parsed = SiteTypeahead.parse(site);
          return { id: parsed.id, name: parsed.name };
        });

        return { data: sites, pagination: extractPagination(response) };
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.sites.failed_to_fetch'));
        return { data: [] };
      }
    },
    [companyId],
  );

  const accountId = watchedAccount?.id ?? '';
  const projectSiteId = watchedProjectSite?.id ?? '';

  const shouldAutoSeedMaterialRate =
    flags.projectMaterialRateSeeding && canManageMaterialRates && isBlankCreate;

  // Snapshot of the row the seeding effect owns for this section (null until
  // it seeds one). Ownership is value-based — see rowMatchesMachineSnapshot:
  // the machine may only overwrite or remove a row whose current values are
  // exactly what the machine last wrote.
  const machineSeededSnapshotRef = useRef<MachineSeededRowSnapshot | null>(null);

  // Latched once the seeded row is edited or removed by the user — this
  // section then never seeds again (a user-touched row is never re-adopted).
  const userBrokeMachineOwnershipRef = useRef(false);

  // Guards out-of-order typeahead responses (same pattern as the order form's
  // MaterialRateDetails): only the latest request may apply.
  const seedRequestKeyRef = useRef(0);

  // The form outlives this section: removing a material total unmounts it, but
  // an in-flight resolution's closure can still append into the live form
  // under a reused parentIndex. Invalidate on unmount ONLY — a per-render
  // cleanup would also run on unrelated dependency changes and wrongly kill a
  // legitimate in-flight resolution.
  useEffect(() => {
    const requestKeyRef = seedRequestKeyRef;
    return () => {
      requestKeyRef.current += 1;
    };
  }, []);

  const findMachineSeededRowIndex = useCallback(() => {
    const snapshot = machineSeededSnapshotRef.current;
    if (!snapshot) return null;

    const rows = getValues('materialRates') ?? [];
    const machineRowIndex = rows.findIndex(
      (row) =>
        !isNil(row) &&
        !row.id &&
        !row.isDeleted &&
        row.parentIndex === parentIndex &&
        rowMatchesMachineSnapshot(row, snapshot),
    );

    return machineRowIndex === -1 ? null : machineRowIndex;
  }, [getValues, parentIndex]);

  // Reconciles the snapshot with reality before any machine write. A snapshot
  // whose row vanished means one of two things, told apart by company:
  // - same company → the user edited or removed the seeded row: latch
  //   ownership broken for good ('stop').
  // - different company → the company cascade wiped the materialRates array
  //   wholesale: forget the stale snapshot but keep seeding alive for the new
  //   company ('continue').
  const reconcileMachineOwnership = useCallback(() => {
    const snapshot = machineSeededSnapshotRef.current;
    if (!snapshot) return 'continue';
    if (findMachineSeededRowIndex() !== null) return 'continue';

    machineSeededSnapshotRef.current = null;

    const rowVanishedUnderSameCompany = snapshot.companyId === companyId;
    if (rowVanishedUnderSameCompany) {
      userBrokeMachineOwnershipRef.current = true;
      return 'stop';
    }

    return 'continue';
  }, [companyId, findMachineSeededRowIndex]);

  const resolveSeededMaterialRate = useCallback(async () => {
    const requestKey = seedRequestKeyRef.current + 1;
    seedRequestKeyRef.current = requestKey;

    try {
      const response = await getCompanyMaterialRatesTypeahead({
        companyId,
        materialId,
        customerId: accountId || undefined,
        siteId: projectSiteId || undefined,
      });
      const responseIsStale = requestKey !== seedRequestKeyRef.current;
      if (responseIsStale) return;

      // The seeded row may have been deleted while this request was in
      // flight; a same-company disappearance means the user removed it, so
      // the response must be dropped rather than resurrecting the row.
      if (reconcileMachineOwnership() === 'stop') return;

      const candidates = response.data;
      // The backend orders candidates most-specific-first (TRE-16144), so the
      // first row is the resolved rate for this customer/site/material.
      const resolvedRate = candidates[0];
      // The typeahead endpoint cannot filter by unit of measure; when the
      // candidates span more than one, seeding any single row would persist
      // an arbitrary UoM on the project.
      const distinctUnitsOfMeasure = new Set(
        candidates.map((candidate) => candidate.unitOfMeasure),
      );
      const resolutionIsAmbiguous = distinctUnitsOfMeasure.size > 1;

      const machineRowIndex = findMachineSeededRowIndex();

      if (!resolvedRate || resolutionIsAmbiguous) {
        // Never leave a stale machine-seeded price behind: a row this effect
        // created, this effect may remove. User rows never match the snapshot
        // and are never touched.
        if (machineRowIndex !== null) {
          removeMaterialRate(machineRowIndex);
        }
        machineSeededSnapshotRef.current = null;
        return;
      }

      // A nil price must stay an empty field: `Number(null).toFixed(2)` is
      // "0.00", which the dialog's submit pipeline would persist as a real
      // zero price. Format only present values (matches formatPriceOnBlur).
      const seededBuyPrice = isNil(resolvedRate.buyPrice)
        ? ''
        : Number(resolvedRate.buyPrice).toFixed(2);
      const seededSellPrice = isNil(resolvedRate.sellPrice)
        ? ''
        : Number(resolvedRate.sellPrice).toFixed(2);
      const seededSite = {
        id: resolvedRate.site?.id ?? '',
        name: resolvedRate.site?.name ?? '',
      };

      // Unit preference, identical for the update and append paths: a unit the
      // parent material row carries always wins; otherwise the resolution's
      // unit (unambiguous thanks to the multi-UoM guard). Read the parent at
      // apply time, not from the dispatch-time closure — it can change while
      // the request is in flight. Never key off the machine row's own unit:
      // the machine stamped it, so a re-resolution must be free to refresh it
      // alongside the prices it belongs to.
      const parentUnitAtApplyTime = getValues(
        `projectMaterialTotals.${parentIndex}.unitOfMeasure`,
      );
      const parentHasNoUnit = !parentUnitAtApplyTime?.id;
      const seededUnitOfMeasure =
        parentHasNoUnit && resolvedRate.unitOfMeasure
          ? { id: resolvedRate.unitOfMeasure, name: resolvedRate.unitOfMeasure }
          : {
              id: parentUnitAtApplyTime?.id ?? '',
              name: parentUnitAtApplyTime?.name ?? '',
            };

      if (machineRowIndex !== null) {
        const currentRow = getValues(`materialRates.${machineRowIndex}`);
        if (isNil(currentRow)) return;

        updateMaterialRate(machineRowIndex, {
          ...currentRow,
          material: { id: materialId, name: materialName },
          materialRateSite: seededSite,
          materialRateBuyPrice: seededBuyPrice,
          materialRateSellPrice: seededSellPrice,
          unitOfMeasure: seededUnitOfMeasure,
        } as never);
        machineSeededSnapshotRef.current = {
          companyId,
          materialId,
          materialRateBuyPrice: seededBuyPrice,
          materialRateSellPrice: seededSellPrice,
          materialRateSiteId: seededSite.id,
          unitOfMeasureId: seededUnitOfMeasure?.id ?? '',
        };
        return;
      }

      // Auto-append only into an empty section: rows the user created (or
      // that edit/copy loaded) are theirs — the effect never adds a competing
      // seeded row next to them.
      const sectionHasRows = (getValues('materialRates') ?? []).some(
        (row) => !isNil(row) && !row.isDeleted && row.parentIndex === parentIndex,
      );
      if (sectionHasRows) return;

      appendMaterialRate({
        ...EmptyMaterialRateItem,
        parentIndex,
        material: { id: materialId, name: materialName },
        unitOfMeasure: seededUnitOfMeasure,
        materialRateSite: seededSite,
        materialRateBuyPrice: seededBuyPrice,
        materialRateSellPrice: seededSellPrice,
      } as never);
      machineSeededSnapshotRef.current = {
        companyId,
        materialId,
        materialRateBuyPrice: seededBuyPrice,
        materialRateSellPrice: seededSellPrice,
        materialRateSiteId: seededSite.id,
        unitOfMeasureId: seededUnitOfMeasure.id,
      };
    } catch (error) {
      // A failure from a superseded context is as stale as its success would
      // be — a newer resolution already owns the section, so don't toast or
      // report it.
      const failureIsStale = requestKey !== seedRequestKeyRef.current;
      if (failureIsStale) return;

      try {
        connection.handleRequestError(
          error,
          t('error_messages.material_rates.failed_to_fetch_material_rates'),
        );
      } catch {
        // Already surfaced (toast + Sentry). Seeding is best-effort: the
        // section stays usable without a seeded rate.
      }
    }
  }, [
    accountId,
    appendMaterialRate,
    companyId,
    findMachineSeededRowIndex,
    getCompanyMaterialRatesTypeahead,
    getValues,
    materialId,
    materialName,
    parentIndex,
    projectSiteId,
    reconcileMachineOwnership,
    removeMaterialRate,
    updateMaterialRate,
  ]);

  // Auto-seed whenever the resolution context (company, customer, project
  // site, material) changes — in any order, matching how dispatchers fill the
  // form. The company belongs in the key even though it alone never resolves a
  // rate: this child effect runs BEFORE the parent Provider's cascade clears
  // account/site/material on a company switch, and the key bump below must
  // invalidate the old company's in-flight response in that window (otherwise
  // it can settle against a wiped section and misread it as a user delete).
  // The ref starts at the mount-time context so mount itself never seeds, and
  // is updated only once seeding is enabled, so a change made while the
  // feature flag is still loading is not consumed (see the effect below).
  const previousSeedContextRef = useRef(
    `${companyId}|${accountId}|${projectSiteId}|${materialId}`,
  );

  useEffect(() => {
    const seedContext = `${companyId}|${accountId}|${projectSiteId}|${materialId}`;
    const previousSeedContext = previousSeedContextRef.current;
    if (seedContext === previousSeedContext) return;

    // Split flags load asynchronously, so this section can render disabled and
    // flip enabled moments later. While disabled, leave the ref stale instead
    // of consuming the change — the flag is an effect dependency, so when it
    // arrives the effect re-fires and the un-consumed change seeds then. No
    // request can be in flight while disabled, so skipping the key bump below
    // loses nothing.
    if (!shouldAutoSeedMaterialRate) return;

    previousSeedContextRef.current = seedContext;

    // A response still in flight belongs to the previous context — invalidate
    // it even on paths that dispatch no new request (e.g. the company cascade
    // just cleared the context), or it would settle and seed stale data.
    seedRequestKeyRef.current += 1;

    // A company change reaches this child effect BEFORE the parent Provider's
    // cascade wipes account/site/material, so the other context dimensions are
    // still the OLD company's. Dispatching here would resolve the new company
    // against that stale context — invalidate + reconcile only, and let the
    // post-cascade context change drive the next resolution. The ownership
    // latch expressed intent about the OLD company's rows, so it resets here:
    // the new company starts fresh instead of inheriting a permanently
    // silenced section.
    const previousCompanyId = previousSeedContext.split('|')[0];
    if (companyId !== previousCompanyId) {
      userBrokeMachineOwnershipRef.current = false;
      reconcileMachineOwnership();
      return;
    }

    if (userBrokeMachineOwnershipRef.current) return;

    if (reconcileMachineOwnership() === 'stop') return;

    if (!materialId) {
      // Material cleared: a machine-owned row would persist a stale price,
      // so it goes too.
      const machineRowIndex = findMachineSeededRowIndex();
      if (machineRowIndex !== null) {
        removeMaterialRate(machineRowIndex);
      }
      machineSeededSnapshotRef.current = null;
      return;
    }

    const sectionHasRows = (getValues('materialRates') ?? []).some(
      (row) => !isNil(row) && !row.isDeleted && row.parentIndex === parentIndex,
    );
    const sectionOnlyHasUserRows = !machineSeededSnapshotRef.current && sectionHasRows;
    if (sectionOnlyHasUserRows) return;

    void resolveSeededMaterialRate();
  }, [
    companyId,
    accountId,
    projectSiteId,
    materialId,
    shouldAutoSeedMaterialRate,
    findMachineSeededRowIndex,
    getValues,
    parentIndex,
    reconcileMachineOwnership,
    removeMaterialRate,
    resolveSeededMaterialRate,
  ]);

  const addRate = useCallback(() => {
    // `EmptyMaterialRateItem` carries empty-string `materialRate*Price`
    // values so the yup `.transform()` round-trip works at validate time;
    // the InferType view types those fields as `number | undefined`. Cast
    // the constructed row via the same `as never` bridge used elsewhere
    // in this file — same runtime shape legacy uses.
    appendMaterialRate({
      ...EmptyMaterialRateItem,
      parentIndex,
      material: { id: materialId, name: materialName },
      unitOfMeasure: { id: unitOfMeasureId, name: unitOfMeasureName },
    } as never);
  }, [
    appendMaterialRate,
    materialId,
    materialName,
    parentIndex,
    unitOfMeasureId,
    unitOfMeasureName,
  ]);

  const removeRate = useCallback(
    (fieldIndex: number) => {
      const current = watchedRates[fieldIndex];
      if (current?.id) {
        // Saved rate — soft-delete so the submit pipeline (dialog's
        // processProjectMaterialRates) emits a DELETE.
        updateMaterialRate(fieldIndex, {
          ...current,
          id: current.id,
          isDeleted: true,
          parentIndex: current.parentIndex ?? parentIndex,
        });
      } else {
        removeMaterialRate(fieldIndex);
      }
    },
    [watchedRates, updateMaterialRate, removeMaterialRate, parentIndex],
  );

  if (!canManageMaterialRates) return null;

  const parentMaterialError =
    formState.errors.projectMaterialTotals?.[parentIndex]?.material;
  const materialRateCoverageError =
    parentMaterialError?.type === MATERIAL_RATE_COVERAGE_ERROR_TYPE
      ? parentMaterialError.message
      : undefined;

  return (
    <Box sx={{ mt: 1 }} data-test-id={`project-form-v2-material-rates-${parentIndex}`}>
      {ratesForThisMaterial.length > 0 && materialId && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, letterSpacing: 1, whiteSpace: 'nowrap' }}
          >
            {t('administration.material_rates.title')}
          </Typography>

          <Divider sx={{ flex: 1 }} />
        </Box>
      )}

      {ratesForThisMaterial.map(({ rate, fieldIndex }) => {
        const fieldItem = materialRateFields[fieldIndex];
        if (!fieldItem) return null;

        const rateHasData =
          Boolean(rate?.materialRateSite?.id) ||
          (rate?.materialRateBuyPrice !== undefined &&
            String(rate.materialRateBuyPrice).trim() !== '') ||
          (rate?.materialRateSellPrice !== undefined &&
            String(rate.materialRateSellPrice).trim() !== '');

        return (
          <Grid
            key={fieldItem.id}
            container
            spacing={MATERIAL_RATE_ROW_SPACING}
            data-test-id={`project-form-v2-material-rate-row-${parentIndex}-${fieldIndex}`}
            sx={(theme) => ({
              bgcolor: theme.semantic.surfaceSunken,
              borderRadius: 2,
              mb: 2.5,
              mt: -MATERIAL_RATE_ROW_SPACING,
              mx: 0,
              pb: 1,
              pl: MATERIAL_RATE_ROW_SPACING,
              position: 'relative',
              pr: 2,
              pt: MATERIAL_RATE_ROW_SPACING,
              width: '100%',
            })}
          >
            <Grid size={{ xs: 6 }}>
              <AutocompleteAsyncFormField
                asyncCallback={fetchSites}
                clearable
                control={control}
                errors={formState.errors}
                getLabel={(item: { id?: string; name?: string } | null) =>
                  item?.name ?? ''
                }
                getValue={(item: { id?: string; name?: string } | null) => item?.id ?? ''}
                label={t('form_fields.site')}
                name={`materialRates.${fieldIndex}.materialRateSite`}
              />
            </Grid>

            <Grid size={{ xs: 3 }}>
              <TextFormField
                control={control}
                errors={formState.errors}
                inputProps={{ step: '0.01' }}
                label={t('administration.material_rates.buy_price')}
                name={`materialRates.${fieldIndex}.materialRateBuyPrice`}
                onBlur={formatPriceOnBlur(
                  `materialRates.${fieldIndex}.materialRateBuyPrice`,
                )}
                type="number"
              />
            </Grid>

            <Grid size={{ xs: 3 }}>
              <TextFormField
                control={control}
                errors={formState.errors}
                inputProps={{ step: '0.01' }}
                label={t('administration.material_rates.sell_price')}
                name={`materialRates.${fieldIndex}.materialRateSellPrice`}
                onBlur={formatPriceOnBlur(
                  `materialRates.${fieldIndex}.materialRateSellPrice`,
                )}
                type="number"
              />
            </Grid>

            <Grid size={{ xs: 4 }} sx={{ mt: -2 }}>
              <AutocompleteFormField
                clearable
                control={control}
                errors={formState.errors}
                getLabel={(item) => item?.name || ''}
                getValue={(item) => item?.id}
                inputProps={{ sx: { background: 'transparent' } }}
                isRequired={rateHasData}
                label={t('form_fields.unit')}
                list={unitOfMeasureOptions}
                name={`materialRates.${fieldIndex}.unitOfMeasure`}
              />
            </Grid>

            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                transform: 'translate(50%, -50%)',
              }}
            >
              <RemoveItemButton
                action={() => removeRate(fieldIndex)}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Grid>
        );
      })}

      {materialRateCoverageError && (
        <Typography
          variant="body2"
          sx={(theme) => ({ color: theme.palette.brandV2Red.main, mb: 1 })}
        >
          {materialRateCoverageError}
        </Typography>
      )}

      <PseudoLink
        action={addRate}
        dataTestId={`project-form-v2-material-rates-add-${parentIndex}`}
        disabled={!materialId}
        sx={{ display: 'inline-flex' }}
        title={t('project.form.add_material_rate')}
      />
    </Box>
  );
}
